import {
  PkOverlay,
  PkSimpleHeader,
  PkButton,
  PkSidePanel,
  PkIcon,
  PkAccordionGroup,
  PkIdentifier,
} from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useMemo, useState, useRef, useEffect, useCallback } from 'react';

import { filterTypes } from '../../configuration/filterTypes';
import { useStatisticFilters } from '../../hooks/filters';
import { useStatisticOptions } from '../../store';
import type { StatsFilters } from '../../store/types';

import FilterPhase from './filter/phase';
import FilterTeam from './filter/team';

import { useWindowSize } from '@hooks/useWindowSize';
import { useTranslateFn } from '@shared/Translate';
import type { RoundPhase } from '@shared-types/match.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';

import * as styles from './styles.module.scss';

const FilterContent = ({ filterNames, handleFilterChange, disableTournamentFilters }) => {
  const { filters } = useStatisticFilters();
  const {
    options: { disablePhaseFilter },
  } = useStatisticOptions();

  const phase = useMemo(() => filters?.phase, [filters?.phase]);
  const teamId = useMemo(() => filters?.teamId, [filters?.teamId]);

  return (
    <div>
      <PkAccordionGroup multiple-open>
        {!disablePhaseFilter && filterNames.includes('phase') && (
          <FilterPhase
            disableTournamentFilters={disableTournamentFilters}
            onFilterChange={handleFilterChange('phase')}
            filter={phase}
          />
        )}
        {filterNames.includes('teamId') && <FilterTeam onFilterChange={handleFilterChange('teamId')} filter={teamId} />}
      </PkAccordionGroup>
    </div>
  );
};

const PanelContent = ({ filterOpened, closeFilter, handleFilterChange }) => {
  const t = useTranslateFn();
  const { options } = useStatisticOptions();
  const filterNames = useMemo(() => filterTypes[options.statType], [options.statType]);

  return (
    <>
      {filterOpened && <div onClick={closeFilter} className={styles.sidePanelOverlay} aria-hidden />}
      <PkSidePanel
        position='right'
        paddingTop={24}
        desktopWidth='256px'
        isOpen={filterOpened}
        className={styles.sidePanel}
      >
        <div slot='content' className={styles.sidePanel}>
          <div className={styles.sidePanelHeader}>
            {t('filter_by')}
            <PkIcon onClick={closeFilter} role='button' name='close-web-icon' />
          </div>
          <div className={styles.sidePanelContent}>
            <FilterContent
              disableTournamentFilters={options.disableTournamentFilters}
              filterNames={filterNames}
              handleFilterChange={handleFilterChange}
            />
          </div>
        </div>
      </PkSidePanel>
    </>
  );
};

const OverlayContent = ({ filterOpened, closeFilter, applyFilters, handleFilterChange }) => {
  const { options } = useStatisticOptions();
  const filterNames = useMemo(() => filterTypes[options.statType], [options.statType]);

  const t = useTranslateFn();

  return (
    <PkOverlay isOpen={filterOpened} paddingBottom='45px'>
      <PkSimpleHeader
        onPkClose={closeFilter}
        slot='header'
        className={styles.header}
        titleColor='ui-01'
        text={t('filter_by')}
      />
      <FilterContent
        disableTournamentFilters={options.disableTournamentFilters}
        filterNames={filterNames}
        handleFilterChange={handleFilterChange}
      />
      <div slot='footer' className='button-footer'>
        <PkButton type='button' onPkClick={applyFilters}>
          {t('apply')}
        </PkButton>
      </div>
    </PkOverlay>
  );
};

const ExtraLargeContent = ({ handleFilterChange }) => {
  const t = useTranslateFn();
  const { options } = useStatisticOptions();
  const filterNames = useMemo(() => filterTypes[options.statType], [options.statType]);

  return (
    <div className={styles.container}>
      <PkIdentifier className={clsx(styles.identifier, 'pk-py--l')}>
        <span slot='prefix'>
          <PkIcon color='text-03' name='actions-filter' size={24} />
        </span>
        <span slot='primary'>{t('filter_by')}</span>
      </PkIdentifier>

      <FilterContent
        disableTournamentFilters={options.disableTournamentFilters}
        filterNames={filterNames}
        handleFilterChange={handleFilterChange}
      />
    </div>
  );
};

export const Filters = () => {
  const t = useTranslateFn();

  const { options } = useStatisticOptions();

  const filterNames = useMemo(
    () => filterTypes[options.statType].filter((name) => !(options.disablePhaseFilter && name === 'phase')),
    [options.statType, options.disablePhaseFilter],
  );

  const { filters, setFilters } = useStatisticFilters();

  const [filterOpened, setFilterOpened] = useState(false);
  const localFilters = useRef<StatsFilters>(filters);

  const { isMobile, isDesktopExtraLarge } = useWindowSize();

  const sidePanelOn: boolean = !isMobile && !isDesktopExtraLarge;
  const overlayOn: boolean = isMobile;

  useEffect(() => {
    localFilters.current = filters;
  }, [filters]);

  const saveFilters = useCallback(() => {
    setFilters(localFilters.current);
  }, [setFilters]);

  const closeFilter = () => {
    setFilterOpened(false);
    document.body.classList.remove('full-screen');
  };

  const openFilter = () => {
    setFilterOpened(true);
    overlayOn && document.body.classList.add('full-screen');
  };

  const applyFilters = () => {
    saveFilters();
    closeFilter();
  };

  const handleFilterChange = useCallback(
    (name: keyof StatsFilters) => (value: number | RoundPhase) => {
      localFilters.current = { ...localFilters.current, [name]: value };
      GoogleTagManagerHelper.event(
        'Click',
        `Change ${name} - ${window.competitionCode} ${value}`,
        'stats',
        { section_reference: 'stats', interaction: `Change ${name} - ${window.competitionCode} ${value}` },
        `change_${name}`,
      );

      if (!overlayOn) {
        saveFilters();
      }
    },
    [overlayOn, saveFilters],
  );

  return (
    <>
      {(sidePanelOn || overlayOn) && (
        <PkButton onPkClick={openFilter} slot='button' appearance='text' type='button' className={styles.btnFilters}>
          <PkIcon color='interaction' name='actions-filter' size={24} />
          {filterNames.length} {t(filterNames.length > 1 ? 'filters' : 'filter')}
        </PkButton>
      )}

      {sidePanelOn && (
        <PanelContent filterOpened={filterOpened} closeFilter={closeFilter} handleFilterChange={handleFilterChange} />
      )}
      {overlayOn && (
        <OverlayContent
          filterOpened={filterOpened}
          closeFilter={closeFilter}
          applyFilters={applyFilters}
          handleFilterChange={handleFilterChange}
        />
      )}
      {!(sidePanelOn || overlayOn) && <ExtraLargeContent handleFilterChange={handleFilterChange} />}
    </>
  );
};

export default memo(Filters);
